import { useEffect, useRef } from 'react';
import { useMutation } from '@tanstack/react-query';

import { useMfaModalContext } from '../../shared/providers/MfaModalProvider';
import {
  addItemToLocalstorage,
  getItemFromLocalStorage,
} from '../common/storage';
import { StorageItems } from '../../shared/constants/app';
import {
  authenticate,
  devicePoll,
  self,
  verifyCode,
} from '../../shared/service/auth';
import {
  BiometricPoll,
  FACTORS,
  MaxApiLimit,
  MfaModalState,
  VerifyToken,
} from '../../shared/constants/MFA';
import useLogout from '../common/hooks/useLogout';
import { decodeJWT } from '../common/encryption';
import useFetch from './useFetch';

export const useMfa = () => {
  // @ts-ignore
  const pullIntervalRef = useRef<null | NodeJS.Timeout>(null);
  const {
    token,
    setToken,
    stateToken,
    setActiveModal,
    setStateToken,
    setQrCodeData,
    setMaxApiLimit,
    setPullingInterval,
    pullingInterval,
    setAccessToken,
    verifyCodeArgs,
    setVerifyCodeArgs,
    invalidStateToken,
    setInvalidStateToken,
    newQrCodeData,
    setInvalidAuthCode,
    setInvalidAuthToken,
    setIsMfaLoading,
    tfaCode,
    setAllowAccess,
  } = useMfaModalContext();

  const { logout } = useLogout();

  useEffect(() => {
    setVerifyCodeArgs((prevArgs: any) => ({
      ...prevArgs,
      token: token,
      stateToken: stateToken || '',
      authCode: tfaCode,
      factorType: FACTORS.TOTP,
    }));
  }, [token, stateToken]);

  useEffect(() => {
    setVerifyCodeArgs((prevVerifyCodeArgs: any) => ({
      ...prevVerifyCodeArgs,
      authCode: tfaCode,
    }));
  }, [tfaCode]);

  const {
    mutate: refetchMfaCheck,
    isPending: isAuthenticating,
    data: authMeData,
  } = useMutation({
    mutationKey: ['mfaSelfAuth'],
    // Authorization (Identity access token) and x-access-token (MFA token) are
    // attached by the request interceptor.
    mutationFn: () => self(),

    onSuccess: (selfAuthData: any) => {
      setToken(selfAuthData?.data?.token);
      const accessToken = getItemFromLocalStorage(
        StorageItems.ACCESS_TOKEN,
        'string',
      );
      const isAccessTokenPresent =
        accessToken !== null && accessToken !== undefined && accessToken !== '';

      if (!isAccessTokenPresent && selfAuthData?.data?.mfaEnabled) {
        setAllowAccess({ login: false, layout: false });
        refetchMfaActiveCheck();
      }

      if (!selfAuthData?.data?.mfaEnabled) {
        setAllowAccess({ login: true, layout: true });
      }
    },
    onError: (response: { data: any }) => {
      if (response?.data?.override?.code === VerifyToken.INVALID_ACCESS_TOKEN) {
        setAllowAccess({ login: false, layout: false });
        refetchMfaActiveCheck();
      }
    },
  });

  function handleFactor(item: any) {
    setStateToken(item.stateToken);
    if (item.factorType === FACTORS.TOTP) {
      setQrCodeData(item);
      if (item.mfaActive) {
        setActiveModal(MfaModalState.Enter2FaCode);
      } else {
        if (!invalidStateToken) setActiveModal(MfaModalState.Setup2Fa);
      }
    }
    if (item.factorType === FACTORS.BIOMETRIC) {
      setActiveModal(MfaModalState.NotificationSent);
      setPullingInterval(true);
    }
  }

  const { mutate: refetchMfaActiveCheck, isPending: loadingMfaActive } =
    useMutation({
      mutationKey: ['checkMfaActive'],
      mutationFn: () => authenticate(token),
      onSuccess: async (res: any) => {
        setMaxApiLimit(false);
        const primaryAuth = res?.data?.primaryAuth;

        if (primaryAuth !== null) {
          for (const item of res?.data?.factors) {
            if (item.id === primaryAuth) {
              const updatedVerifyCodeArgs = {
                ...verifyCodeArgs,
                stateToken: item.stateToken || '',
              };
              setVerifyCodeArgs(updatedVerifyCodeArgs);
              handleFactor(item);
              break;
            }
          }
        } else {
          if (
            res?.data?.factors.length === 1 &&
            !res?.data?.factors[0]?.isEnabled
          ) {
            const updatedVerifyCodeArgs = {
              ...verifyCodeArgs,
              stateToken: res?.data?.factors[0].stateToken || '',
            };
            setVerifyCodeArgs(updatedVerifyCodeArgs);
            handleFactor(res?.data?.factors[0]);
          } else {
            for (const item of res?.data?.factors) {
              if (item.isEnabled) {
                const updatedVerifyCodeArgs = {
                  ...verifyCodeArgs,
                  stateToken: item.stateToken || '',
                };
                setVerifyCodeArgs(updatedVerifyCodeArgs);
                handleFactor(item);
                break;
              }
            }
          }
        }
      },
      onError: async (err: any) => {
        if (err?.data?.override?.code === MaxApiLimit.MAX_API_LIMIT) {
          setMaxApiLimit(true);
          logout();
          setActiveModal(MfaModalState.NoModal);
        }
      },
    });

  const { mutate: refetchMfaVerifyAuthCode, isPending: verifyCodeLoading } =
    useMutation({
      mutationKey: ['mfaVerifyAuthCode'],
      mutationFn: () => verifyCode(verifyCodeArgs),
      onSuccess: (response: any) => {
        if (response?.data?.accessToken) {
          setAccessToken(response?.data?.accessToken);
          addItemToLocalstorage(
            StorageItems.ACCESS_TOKEN,
            response?.data?.accessToken,
          );
          if (newQrCodeData) {
            if ('keyUri' in newQrCodeData) {
              setActiveModal(MfaModalState.Enabled2Fa);
              setAllowAccess({ login: false, layout: false });
            } else {
              setActiveModal(MfaModalState.NoModal);
              setAllowAccess({ login: true, layout: true });
            }
          } else {
            setActiveModal(MfaModalState.NoModal);
            setAllowAccess({ login: true, layout: true });
          }
        }
      },
      onError: (response: { error: any }) => {
        // Invalid authCode
        if (response?.error?.override?.code === VerifyToken.INVALID_AUTH_CODE) {
          setInvalidStateToken(false);
          setInvalidAuthCode(true);
        }
        // Invalid stateToken
        if (
          response?.error?.override?.code === VerifyToken.INVALID_STATE_TOKEN
        ) {
          if (newQrCodeData) {
            setInvalidStateToken(true);
            setTimeout(() => {
              //  eslint-disable-next-line @typescript-eslint/no-use-before-define
              refetchMfaActiveCheck();
            }, 100);
          } else {
            setInvalidAuthToken(true);
          }
        }
      },
    });

  const refetchDevicePollArgs = {
    token: token || '',
    stateToken,
  };
  const { refetch: refetchDevicePoll } = useFetch(
    ['checkDevicePoll'],
    () => devicePoll(refetchDevicePollArgs),
    {
      enabled: false,
    },
  );

  function pullingBiometric() {
    if (pullingInterval) {
      pullIntervalRef.current = setInterval(() => {
        const pollStatus = refetchDevicePoll();
        pollStatus.then(async (res) => {
          if (res?.data?.data?.status === BiometricPoll.APPROVED) {
            addItemToLocalstorage(
              StorageItems.ACCESS_TOKEN,
              res?.data?.data?.accessToken,
            );
            setActiveModal(MfaModalState.NoModal);

            setAllowAccess({ login: true, layout: true });

            if (pullIntervalRef.current) {
              clearInterval(pullIntervalRef.current);
            }
          }
          if (
            res?.data?.data?.status === BiometricPoll.REJECTED ||
            res?.data?.data?.status === BiometricPoll.INACTIVE
          ) {
            logout();
          }
        });
      }, 5000);
    }
  }

  useEffect(() => {
    if (pullingInterval) pullingBiometric();
    else if (pullIntervalRef.current) clearInterval(pullIntervalRef.current);
    return () => {
      if (pullIntervalRef.current) {
        clearInterval(pullIntervalRef.current);
      }
    };
  }, [pullingInterval]);

  useEffect(() => {
    setIsMfaLoading(isAuthenticating || loadingMfaActive || verifyCodeLoading);
  }, [isAuthenticating, loadingMfaActive, verifyCodeLoading]);

  return {
    refetchMfaCheck,
    refetchMfaVerifyAuthCode,
    authMeData,
    isAccessTokenValid: () => {
      const accessToken = getItemFromLocalStorage(
        StorageItems.ACCESS_TOKEN,
        'string',
      );
      let tokenData: any;
      try {
        tokenData = decodeJWT(accessToken as string);
      } catch (e) {
        return false;
      }
      return tokenData && tokenData.exp > new Date().getTime() / 1000;
    },
  };
};
